import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

export interface RerunPromptOption {
	id: number;
	name: string;
}

interface RerunResearchDialogProps {
	prompts: RerunPromptOption[];
	activePromptId: number | null;
	currentPromptId: number | null;
	isExecuting: boolean;
	lastResearchedAt: string | null;
	creditLabel: string;
	onPromptChange: (promptId: number) => void;
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * Re-run confirmation with the template picker.
 *
 * Rendered **inline, inside the panel's DOM** rather than portaled to the body:
 * the detail view closes on outside clicks, and a portaled dialog reads as one.
 * The overlay is scoped to the panel via the positioned wrapper around it.
 */
export const RerunResearchDialog: FunctionComponent<RerunResearchDialogProps> = ({
	prompts,
	activePromptId,
	currentPromptId,
	isExecuting,
	lastResearchedAt,
	creditLabel,
	onPromptChange,
	onCancel,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const [hasMultipleTemplates] = useState(prompts.length > 1);

	return (
		<div
			className="absolute inset-0 z-[60] flex items-start justify-center bg-black/30 p-4"
			data-testid="research-rerun-dialog"
		>
			<div className="w-full max-w-md rounded-xl border border-border-light bg-white shadow-xl">
				<div className="px-4 pb-2 pt-4">
					<h4 className="text-sm font-bold text-text">
						{t("profileTracker.deepResearch.rerunTitle")}
					</h4>
					<p className="mt-1 text-xs leading-relaxed text-text-meta">
						{hasMultipleTemplates
							? t("profileTracker.deepResearch.rerunDescriptionWithTemplates")
							: t("profileTracker.deepResearch.rerunDescription")}
					</p>
				</div>

				{hasMultipleTemplates && (
					<div className="flex max-h-56 flex-col gap-1.5 overflow-auto px-4 py-2">
						{prompts.map((prompt) => {
							const isSelected = prompt.id === activePromptId;

							return (
								<button
									key={prompt.id}
									className={classNames(
										"flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors",
										isSelected
											? "border-brand-light bg-light-blue"
											: "border-border-light hover:border-brand-light"
									)}
									data-testid={`research-rerun-template-${prompt.id}`}
									type="button"
									onClick={() => onPromptChange(prompt.id)}
								>
									<span
										className={classNames(
											"flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
											isSelected ? "border-brand-light" : "border-gray-200"
										)}
									>
										{isSelected && <span className="h-2 w-2 rounded-full bg-brand-light" />}
									</span>
									<span className="min-w-0 flex-1 text-xs font-semibold text-text">
										{prompt.name}
										{prompt.id === currentPromptId && (
											<span className="ml-1.5 rounded border border-status-success/30 bg-status-success-light px-1 text-[10px] font-bold uppercase text-status-success">
												{t("profileTracker.deepResearch.currentTemplate")}
											</span>
										)}
									</span>
								</button>
							);
						})}
					</div>
				)}

				<div className="flex items-center gap-2 border-t border-border-light px-4 py-3">
					<p className="mr-auto text-[11px] leading-relaxed text-text-tertiary">
						{lastResearchedAt
							? t("profileTracker.deepResearch.lastResearchedAt", { date: lastResearchedAt })
							: ""}
					</p>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isExecuting}
						size={ButtonSize.XS}
						title={t("common.cancel")}
						onClick={onCancel}
					/>
					<Button
						color={ButtonColor.BRAND}
						disabled={isExecuting}
						isLoading={isExecuting}
						size={ButtonSize.XS}
						title={`${t("profileTracker.deepResearch.rerun")} · ${creditLabel}`}
						onClick={onConfirm}
					/>
				</div>
			</div>
		</div>
	);
};
